// The project document and the pure rules over it. Copied from the iOS
// Models.swift and owned here; the shapes mirror the iOS archive (flat steps
// with parent links) so a stage 7 import is a read, not a translation.
//
// Sync-ready rules (DECISIONS §10): new fields are optional with a default,
// every edit site stamps editedAt, a deletion is a tombstone (deletedAt), and
// a Step keeps its id through a snag so its done-state and points survive.

export type Id = string;
/** Milliseconds since the epoch. */
export type Millis = number;

export interface Snag {
  note: string;
  photoHash?: string;
  advice: string;
  /** What the step was called before the fix reworded it; absent when the wording survived. */
  previousTitle?: string;
  reportedAt: Millis;
}

export interface Step {
  id: Id;
  title: string;
  done: boolean;
  editedAt?: Millis;
  /** The part of the aim this item serves, named at generation time. */
  bundle?: string;
  /** Equipment only: why the tool or material is needed. */
  reason?: string;
  /** The step this one was broken out of. Sub-steps sit flat in `steps`, right after their parent. */
  parent?: Id;
  snag?: Snag;
}

export interface Video { id: Id; title: string; duration: string; url: string }
export interface Source { id: Id; title: string; url: string }

export interface Inspo {
  id: Id;
  kind: 'image' | 'document';
  name: string;
  photoHash?: string;
  text?: string;
}

export interface Brief {
  subject: string;
  goal: string;
  aspiration?: string;
  observations: string[];
  constraints: string[];
}

export type Stage = 'understand' | 'research' | 'extract' | 'illustrate';
export const STAGES: readonly Stage[] = ['understand', 'research', 'extract', 'illustrate'];

export interface Project {
  id: Id;
  listId?: Id;
  title: string;
  summary: string;
  /** A bundled blueprint name for demo and template projects; a generated blueprint overrides it. */
  artwork?: string;
  steps: Step[];
  equipment: Step[];
  videos: Video[];
  tips: string[];
  /** The original request: the regeneration input. Absent on demo projects. */
  request?: string;
  photoHash?: string;
  inspo?: Inspo[];
  brief?: Brief;
  synthesis?: string;
  sources?: Source[];
  /** Content hash of the generated blueprint in R2 and the media cache. */
  blueprintHash?: string;
  generatedAt?: Millis;
  createdAt?: Millis;
  editedAt?: Millis;
  /** Tombstone. A deleted project is kept, never dropped, so sync can propagate the deletion. */
  deletedAt?: Millis;
}

/** Two lines of a card: the length the extract prompt asks for and every title is trimmed to. */
export const TITLE_LIMIT = 60;

export function titleTrimmed(title: string): string {
  return trimmedAtWordBoundary(title, TITLE_LIMIT);
}

export function trimmedAtWordBoundary(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
}

export const stepsDone = (p: Project): number => p.steps.filter((s) => s.done).length;
export const isComplete = (p: Project): boolean => p.steps.length > 0 && stepsDone(p) === p.steps.length;
export const progress = (p: Project): number => (p.steps.length ? stepsDone(p) / p.steps.length : 0);

/**
 * Whether these steps are the project's own. Every generated project is seeded
 * from a template before the pipeline fills it in, so a run that failed before
 * `extract` carries someone else's checklist under the user's request. Demo
 * projects have no request and stay real.
 */
export const isRealPlan = (p: Project): boolean => p.request === undefined || p.generatedAt !== undefined;

/** The sub-steps broken directly out of `stepId`, in list order. */
export const children = (p: Project, stepId: Id): Step[] => p.steps.filter((s) => s.parent === stepId);

/**
 * The one thing to do next: the first unticked step. A step a snag broke down
 * is stepped over until its sub-steps are ticked; the breakdown is the work.
 */
export function nextStep(p: Project): Step | undefined {
  return p.steps.find((s) => !s.done && children(p, s.id).every((c) => c.done));
}

/** Every step nested under `stepId` at any depth. A parent cycle terminates. */
export function descendants(p: Project, stepId: Id): Step[] {
  const found: Step[] = [];
  const seen = new Set<Id>([stepId]);
  const frontier = [stepId];
  while (frontier.length) {
    const next = frontier.pop()!;
    for (const child of p.steps) {
      if (child.parent === next && !seen.has(child.id)) {
        seen.add(child.id);
        found.push(child);
        frontier.push(child.id);
      }
    }
  }
  return found;
}

/** How far a step sits under the one it was broken out of: 0 for a plan step. Cycle-safe. */
export function depth(p: Project, step: Step): number {
  let d = 0;
  const seen = new Set<Id>([step.id]);
  let current: Step | undefined = step;
  while (current?.parent && !seen.has(current.parent)) {
    seen.add(current.parent);
    const parent = p.steps.find((s) => s.id === current!.parent);
    if (!parent) break;
    d++;
    current = parent;
  }
  return d;
}

/**
 * Every stored photo these projects still point at: input photos, inspo
 * pictures and snag photos share one content-addressed store, and a hash
 * missing from here is a photo the next sweep deletes. Any new place a photo
 * can live is added here in the same change.
 */
export function livePhotoHashes(projects: Project[]): Set<string> {
  const live = new Set<string>();
  for (const p of projects) {
    if (p.photoHash) live.add(p.photoHash);
    for (const i of p.inspo ?? []) if (i.photoHash) live.add(i.photoHash);
    for (const t of [...p.steps, ...p.equipment]) if (t.snag?.photoHash) live.add(t.snag.photoHash);
  }
  return live;
}

/**
 * What a stage still needs from the project before it can run: the missing
 * artifact's name, or undefined when it is ready. The plan screen's menu and
 * the Worker's run route both ask here, so the menu never offers a run the
 * server would refuse (ported from the iOS GenStage.missingInput).
 */
export function missingInput(stage: Stage, p: Project): 'brief' | 'research digest' | undefined {
  switch (stage) {
    case 'understand':
      return undefined;
    case 'research':
    case 'illustrate':
      return p.brief ? undefined : 'brief';
    case 'extract':
      return !p.brief ? 'brief' : p.synthesis === undefined ? 'research digest' : undefined;
  }
}
